import { getCustomerAuthFromCookies } from '@bulknutrients/bulk-shared-lib/lib/api/auth';
import { getCustomerId } from '@bulknutrients/bulk-shared-lib/lib/api/customer';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { Button } from '@components/Button';
import { Container } from '@components/Container';
import { CreateAccountForm } from '@components/CreateAccountForm/CreateAccountForm';
import { StandardLayout } from '@components/Layouts/StandardLayout';
import { LoginModal } from '@components/LoginForm/LoginModal';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { RewardsLink } from '@components/Typography/TextLink/TextLink';
import type { GetServerSideProps } from 'next';
import Head from 'next/head';
import { type ReactElement, useState } from 'react';

export default function CreateAccountPage(): ReactElement {
  const [showLoginModal, setShowLoginModal] = useState(false);

  return (
    <>
      <Head>
        <title>
          Create an account at Bulk Nutrients online supplement store
        </title>
        <meta
          name="title"
          content="Create an account at Bulk Nutrients online supplement store"
        />
        <meta
          name="description"
          content="Create a account at Bulk Nutrients and you'll have access to favourites, order history, saved shipping addresses , Whey Cool loyalty points and more"
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}/create-account`}
        />
      </Head>
      <Container>
        <Breadcrumbs
          crumbs={[
            { title: 'Home', path: '/' },
            { title: 'Create Account', path: '/create-account' },
          ]}
        />
      </Container>
      <Container as="main" id="main-content">
        <LoginModal
          showModal={showLoginModal}
          setShowModal={setShowLoginModal}
          modalTitle="Login to your account"
        />
        <article className="my-0 max-w-[600px] justify-self-center">
          <h1>New Account</h1>
          <Paragraph>
            Already have an account?{' '}
            <Button
              buttonStyle="text"
              className="inline"
              onClick={() => setShowLoginModal(true)}
            >
              Sign in
            </Button>
            !
          </Paragraph>
          <Paragraph>
            If you haven't already made an account, then you're in the right
            place and are about to make an excellent decision by creating one.
          </Paragraph>
          <Paragraph>
            It'll let you checkout faster, create a handy favourites list and
            start racking up <RewardsLink>Whey Cool Points</RewardsLink> with
            every purchase you make!
          </Paragraph>
          <Paragraph>Enough talk though, let's crack on...</Paragraph>
          <CreateAccountForm />
        </article>
      </Container>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({
  params,
  req,
}) => {
  // Get customer id if available
  const { cookies } = req;
  const customerAuth = getCustomerAuthFromCookies(cookies);
  const customerId = customerAuth && (await getCustomerId(customerAuth));

  // If customer, redirect to account order history
  if (customerId) {
    return {
      redirect: {
        destination: '/account/orders',
        permanent: false,
      },
    };
  }

  // return empty props
  return { props: {} };
};

CreateAccountPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};
